import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

class ApiService {
  private api: AxiosInstance
  private baseURL: string
  private authToken: string | null = null

  constructor() {
    // Use environment variable for API URL with smart fallback
    const envApiUrl = import.meta.env.VITE_API_URL
    const isProduction = import.meta.env.PROD === true || import.meta.env.MODE === 'production'
    
    if (envApiUrl) {
      this.baseURL = envApiUrl
    } else if (isProduction) {
      // Production: point to Express backend on Cloud Run (esales-api service)
      this.baseURL = 'https://esales-api-142602456566.us-central1.run.app'
    } else {
      // Development: local backend
      this.baseURL = 'http://localhost:5000'
    }
    
    // Ensure no trailing slash
    this.baseURL = this.baseURL.replace(/\/$/, '')
    
    // Backend routes are under /api - ensure base URL ends with /api
    if (!this.baseURL.endsWith('/api')) {
      this.baseURL = `${this.baseURL}/api`
    }
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000, // 30s - allows for cold starts and slower networks
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      withCredentials: true, // Send cookies for CSRF protection
    })

    this.setupInterceptors()
  }

  setAuthToken(token: string) {
    this.authToken = token
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  clearAuthToken() {
    this.authToken = null
    delete this.api.defaults.headers.common['Authorization']
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        if (this.authToken) {
          config.headers.Authorization = `Bearer ${this.authToken}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401) {
          // Backend has no /auth/refresh - avoid retry loop. Clear all auth and redirect once.
          if (!originalRequest._authHandled) {
            originalRequest._authHandled = true
            this.clearAuthToken()
            localStorage.removeItem('isAuthenticated')
            localStorage.removeItem('user')
            localStorage.removeItem('token')
            const path = window.location.pathname || ''
            if (!path.startsWith('/auth/')) {
              window.location.replace('/auth/login')
            }
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // Generic HTTP methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url, config)
    return response.data
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data, config)
    return response.data
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.patch(url, data, config)
    return response.data
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url, config)
    return response.data
  }

  // File upload
  async upload<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    const response: AxiosResponse<T> = await this.api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })

    return response.data
  }

  // Download file
  async download(url: string, filename?: string): Promise<void> {
    const response = await this.api.get(url, {
      responseType: 'blob',
    })

    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.get('/health')
  }
}

export const apiService = new ApiService()
export default apiService

